/** Display foreground state independently of the connection and background entities. */
import { declaredPresentation } from '@web/adapter/toolProjection'
import type { SessionStatus } from '@web/api/types'
import { toolDisplayName } from '@web/lib/toolNames'
import type { AcpState } from '@web/state/acp'
import { type BridgeState, pendingPermissions } from '@web/state/bridge'

export type WorkTone = 'idle' | 'working' | 'attention' | 'error' | 'off'
export type WorkState = {
  status: SessionStatus | 'unknown'
  tone: WorkTone
  label: string
  detail: string | null
  working: boolean
  runningTools: []
}
export function describeWork(agent: AcpState, bridge: BridgeState): WorkState {
  const base = { runningTools: [] as [], working: agent.running }
  const disconnected: Partial<
    Record<SessionStatus | 'unknown', [string, string | null]>
  > = {
    asleep: ['Asleep', 'Resume to continue'],
    closed: ['Closed', 'Resume to continue'],
    starting: ['Starting', 'Opening your session…'],
    error: ['Connection error', bridge.error],
    exited: ['Ended', null],
    detached: [
      'Disconnected',
      agent.running
        ? 'The agent may still be working.'
        : 'Reconnect to continue',
    ],
    unknown: ['Connecting', null],
  }
  const transport = disconnected[bridge.status]
  if (transport)
    return {
      ...base,
      status: bridge.status,
      tone: bridge.status === 'error' ? 'error' : 'off',
      label: transport[0],
      detail: transport[1],
    }
  const permissions = pendingPermissions(bridge)
  const elicitation = Object.values(agent.elicitations).some(
    (request) => !request.resolved,
  )
  if (
    agent.foreground === 'requires_action' ||
    permissions.length ||
    elicitation
  )
    return {
      ...base,
      status: 'requires_action',
      tone: 'attention',
      label: 'Needs you',
      detail: permissions[0]?.title ?? 'Your input is needed',
    }
  if (!agent.running)
    return {
      ...base,
      status: 'idle',
      tone: 'idle',
      label: 'Idle',
      detail: null,
    }
  const active = Object.values(agent.tools).filter(
    (tool) => tool.status === 'in_progress',
  )
  const activeTool = active.length === 1 ? active[0] : undefined
  return {
    ...base,
    status: 'running',
    tone: 'working',
    label: 'Working',
    detail: activeTool
      ? `Running ${toolDisplayName({
          identifier: activeTool.name,
          title: activeTool.title,
          presentation:
            activeTool.presentation ??
            (activeTool.name
              ? declaredPresentation(activeTool.name, bridge)
              : undefined),
        })}`
      : active.length > 1
        ? `Working with ${active.length} tools`
        : 'Working…',
  }
}
export const effectiveStatus = (
  bridge: BridgeState,
  agent: AcpState,
): SessionStatus | 'unknown' => describeWork(agent, bridge).status
